import { useEffect, useMemo, useState, type FormEvent } from "react";
import { apiFetch } from "../api/client.js";
import { useAppActions } from "../app/AppProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import { AdminKpi } from "./admin/AdminKpi.tsx";
import { DuplicatesList, ImportBatchesList } from "./admin/AdminLists.tsx";
import { AssignLeadForm } from "./admin/AssignLeadForm.tsx";
import { firstSellerEmail } from "./admin/formatters.ts";
import { SellersTable } from "./admin/SellersTable.tsx";
import {
  EMPTY_ASSIGN_FORM,
  type AdminDashboard,
  type AdminDuplicatePair,
  type AdminImportBatch,
  type AdminUser,
  type AdminUserAccess,
  type AdminWhatsappAccount,
  type AssignForm,
} from "./admin/types.ts";
import { WhatsappAccessMatrix } from "./admin/WhatsappAccessMatrix.tsx";

export function Admin() {
  const { state } = useAppActions();
  const { showToast } = useToast();
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [imports, setImports] = useState<AdminImportBatch[]>([]);
  const [duplicates, setDuplicates] = useState<AdminDuplicatePair[]>([]);
  const [waAccounts, setWaAccounts] = useState<AdminWhatsappAccount[]>([]);
  const [userAccess, setUserAccess] = useState<Record<number, AdminUserAccess>>({});
  const [accessSaving, setAccessSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [assignForm, setAssignForm] = useState<AssignForm>(EMPTY_ASSIGN_FORM);
  const isAdmin = state.user?.role === "admin";

  const sellerUsers = useMemo(
    () => users.filter((user) => user.role !== "admin"),
    [users],
  );
  const sellerMetrics = useMemo(
    () => [...(dashboard?.by_seller || [])].sort((a, b) => (b.activities_total || 0) - (a.activities_total || 0)),
    [dashboard],
  );

  useEffect(() => {
    if (!isAdmin) return;
    void loadAdminData();
  }, [isAdmin]);

  async function loadAdminData() {
    setLoading(true);
    setError("");
    try {
      const [dashboardData, usersData, importsData, duplicatesData, waData] = await Promise.all([
        apiFetch("/admin/dashboard-metrics?days=30"),
        apiFetch("/admin/users"),
        apiFetch("/admin/imports"),
        apiFetch("/admin/duplicates"),
        apiFetch("/admin/whatsapp-accounts"),
      ]);
      const usersList = Array.isArray(usersData) ? usersData as AdminUser[] : [];

      setDashboard(dashboardData as AdminDashboard);
      setUsers(usersList);
      setImports(Array.isArray(importsData) ? importsData as AdminImportBatch[] : []);
      setDuplicates(Array.isArray(duplicatesData) ? duplicatesData as AdminDuplicatePair[] : []);
      setWaAccounts(((waData as { accounts?: AdminWhatsappAccount[] })?.accounts || []) as AdminWhatsappAccount[]);

      const accessMap: Record<number, AdminUserAccess> = {};
      await Promise.all(usersList.map(async (user) => {
        try {
          accessMap[user.id] = await apiFetch(`/admin/users/${user.id}/whatsapp-access`) as AdminUserAccess;
        } catch (_) {
          /* noop */
        }
      }));
      setUserAccess(accessMap);
      setAssignForm((current) => ({
        ...current,
        seller_email: current.seller_email || firstSellerEmail(usersData),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao carregar painel admin");
    } finally {
      setLoading(false);
    }
  }

  async function toggleAccess(targetUser: AdminUser, account: AdminWhatsappAccount, currentlyHas: boolean) {
    const key = `${targetUser.id}:${account.sessionId}`;
    setAccessSaving(key);
    setUserAccess((prev) => {
      const cur = prev[targetUser.id] || { userId: targetUser.id, owned: [], grantedSessionIds: [] };
      const granted = new Set(cur.grantedSessionIds);
      if (currentlyHas) granted.delete(account.sessionId);
      else granted.add(account.sessionId);
      return { ...prev, [targetUser.id]: { ...cur, grantedSessionIds: Array.from(granted) } };
    });
    try {
      if (currentlyHas) {
        await apiFetch(`/admin/users/${targetUser.id}/whatsapp-access/${encodeURIComponent(account.sessionId)}`, {
          method: "DELETE",
        });
        showToast(`Acesso de "${account.label || account.sessionId.slice(0, 8)}" removido de ${targetUser.email}`, "info");
      } else {
        await apiFetch(`/admin/users/${targetUser.id}/whatsapp-access`, {
          method: "POST",
          body: JSON.stringify({ accountSessionId: account.sessionId }),
        });
        showToast(`"${account.label || account.sessionId.slice(0, 8)}" liberado pra ${targetUser.email}`, "success");
      }
    } catch (err) {
      setUserAccess((prev) => {
        const cur = prev[targetUser.id] || { userId: targetUser.id, owned: [], grantedSessionIds: [] };
        const granted = new Set(cur.grantedSessionIds);
        if (currentlyHas) granted.add(account.sessionId);
        else granted.delete(account.sessionId);
        return { ...prev, [targetUser.id]: { ...cur, grantedSessionIds: Array.from(granted) } };
      });
      showToast(err instanceof Error ? err.message : "Falha ao alterar acesso", "error");
    } finally {
      setAccessSaving(null);
    }
  }

  function userHasAccess(targetUserId: number, account: AdminWhatsappAccount): boolean {
    if (account.ownerUserId === targetUserId) return true;
    const access = userAccess[targetUserId];
    return !!access && access.grantedSessionIds.includes(account.sessionId);
  }

  async function submitAssignedLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!assignForm.seller_email || !assignForm.name.trim()) {
      showToast("Informe vendedor e nome do lead", "error");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch("/admin/leads/assign", {
        method: "POST",
        body: JSON.stringify(assignForm),
      });
      showToast("Lead atribuído ao vendedor", "info");
      setAssignForm({ ...EMPTY_ASSIGN_FORM, seller_email: assignForm.seller_email });
      await loadAdminData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao atribuir lead", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="admin-shell">
        <section className="admin-empty">
          <h1>Acesso restrito</h1>
          <p>Este painel só está disponível para o e-mail administrador configurado no servidor.</p>
        </section>
      </div>
    );
  }

  const summary = dashboard?.summary || {};

  return (
    <div className="admin-shell">
      <header className="admin-head">
        <div>
          <h1>Admin</h1>
          <p>Operação, vendedores, importações e atribuição manual de leads.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={() => void loadAdminData()} disabled={loading}>
          Atualizar
        </button>
      </header>

      {error ? <div className="admin-alert">{error}</div> : null}

      <section className="admin-kpis" aria-label="Resumo admin">
        <AdminKpi label="Vendedores" value={summary.sellers_total} loading={loading} />
        <AdminKpi label="Leads" value={summary.leads_total} loading={loading} />
        <AdminKpi label="Abertos" value={summary.leads_open} loading={loading} />
        <AdminKpi label="Ganhos 30d" value={summary.wins_in_period} loading={loading} />
        <AdminKpi label="Atividades" value={summary.activities_total} loading={loading} />
        <AdminKpi label="Resposta" value={summary.response_rate} suffix="%" loading={loading} />
      </section>

      <div className="admin-layout">
        <SellersTable sellers={sellerMetrics} loading={loading} />
        <WhatsappAccessMatrix
          users={users}
          accounts={waAccounts}
          loading={loading}
          accessSaving={accessSaving}
          userHasAccess={userHasAccess}
          onToggleAccess={(user, account, has) => void toggleAccess(user, account, has)}
        />
        <AssignLeadForm
          form={assignForm}
          sellers={sellerUsers}
          submitting={submitting}
          onChange={setAssignForm}
          onSubmit={(event) => void submitAssignedLead(event)}
        />
        <ImportBatchesList imports={imports} loading={loading} />
        <DuplicatesList duplicates={duplicates} loading={loading} />
      </div>
    </div>
  );
}
